import { makeStyles, shorthands, tokens } from "@fluentui/react-components";

export const useWebChatStyles = makeStyles({
  /* Toggle button.
   *
   *   Tablet / desktop (>=500px): inline pill rendered by NavigationHeader
   *   Mobile          (<500px) : circular FAB pinned bottom-right with
   *                              safe-area-bottom offset, hides text label.
   *
   * The FAB sits on top of the page (z-index 199, one below the chat panel
   * at 200) and stays accessible across scroll. When the chat panel is
   * open on mobile it covers the FAB visually, which is the desired
   * behaviour — users dismiss via the panel's own close button. */
  toggleButton: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "8px",
    paddingRight: "12px",
    ...shorthands.gap("4px"),
    height: "32px",
    backgroundColor: tokens.colorTransparentBackground,
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    cursor: "pointer",
    fontFamily: "'Segoe UI', sans-serif",
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "20px",
    color: tokens.colorNeutralForeground1,
    ":hover": {
      backgroundColor: tokens.colorSubtleBackgroundHover,
    },
    "@media (max-width: 499px)": {
      position: "fixed",
      right: "16px",
      bottom: "calc(16px + env(safe-area-inset-bottom))",
      zIndex: 199,
      width: "56px",
      height: "56px",
      paddingTop: "0",
      paddingBottom: "0",
      paddingLeft: "0",
      paddingRight: "0",
      ...shorthands.borderRadius("50%"),
      ...shorthands.gap("0"),
      justifyContent: "center",
      backgroundColor: tokens.colorBrandBackground,
      color: tokens.colorNeutralForegroundOnBrand,
      ...shorthands.border("none"),
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      ":hover": {
        backgroundColor: tokens.colorBrandBackgroundHover,
      },
    },
  },
  toggleButtonIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    flexShrink: 0,
    "@media (max-width: 499px)": {
      width: "24px",
      height: "24px",
    },
  },
  toggleButtonText: {
    fontSize: "14px",
    fontWeight: "400",
    lineHeight: "20px",
    color: tokens.colorNeutralForeground1,
    paddingBottom: "2px",
    "@media (max-width: 499px)": {
      // Hide the text label on the FAB; aria-label keeps screen-reader
      // accessibility (verified — WebChatToggleButton sets aria-label
      // on the underlying <button>, so the icon-only FAB still announces
      // its purpose to assistive tech).
      display: "none",
    },
  },
  /* Chat panel — responsive across mobile / tablet / desktop.
   *
   * Breakpoints match the rest of the app (SharedStyles, NavigationHeader,
   * BuildingListView): <500 mobile, 500–1239 tablet, ≥1240 desktop.
   *
   *   Mobile  <500       100vw  · 100dvh  · fullscreen takeover · safe-area top padding
   *   Tablet  500–1239   clamp(400px, 50vw, 560px) right-anchored panel
   *   Desktop ≥1240      400px right-anchored panel (current behaviour)
   *
   * The mobile-fullscreen pattern matches MenuItemDetails and Checkout —
   * other surfaces in this codebase that take over the full viewport on
   * phones rather than overlay (no bottom-sheet, no drag-to-dismiss in v1).
   */
  chatWindow: {
    pointerEvents: "auto",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    top: "0",
    right: "0",
    bottom: "0",
    zIndex: 1001,
    overflowX: "hidden",
    overflowY: "hidden",
    // Fluent v9 Drawer-level elevation. shadow28 is the canonical token for
    // floating side panels and adapts across light/dark themes (includes
    // both inner + outer shadow values in dark mode so the panel reads as
    // elevated against a dark app backdrop instead of disappearing into it).
    boxShadow: tokens.shadow28,
    backgroundColor: tokens.colorNeutralBackground1,
    // Soft inner edge — visible in dark mode where shadow alone is faint,
    // unobtrusive in light mode. Matches the header divider stroke for
    // visual coherence. Removed in the mobile media query below since the
    // panel goes fullscreen there and the left edge is the viewport edge.
    ...shorthands.borderLeft("1px", "solid", tokens.colorNeutralStroke2),

    // Mobile-first defaults: fullscreen takeover. On viewports >= 500px the
    // queries below clamp / lock the width and remove the safe-area top
    // padding (status bar / notch is host-app responsibility on tablet+).
    width: "100vw",
    height: "100vh",
    maxHeight: "100vh",
    paddingTop: "env(safe-area-inset-top)",
    ...shorthands.borderRadius("0px"),

    // Prefer the dynamic viewport unit when supported — fixes iOS Safari /
    // WKWebView so the chat panel and send box don't get clipped behind
    // the browser chrome / on-screen keyboard. Falls back transparently to
    // 100vh on Safari < 15.4, Chromium < 108, Firefox < 101.
    "@supports (height: 100dvh)": {
      height: "100dvh",
      maxHeight: "100dvh",
    },

    // Tablet — fluid right-anchored panel between 400px and 560px.
    "@media (min-width: 500px) and (max-width: 1239px)": {
      width: "clamp(400px, 50vw, 560px)",
      paddingTop: "0",
    },

    // Desktop — locked to the original 400px slab.
    "@media (min-width: 1240px)": {
      width: "400px",
      paddingTop: "0",
    },

    // Mobile (<500): fullscreen takeover. No left edge to delineate, drop
    // the border so we don't paint a stray 1px line against the viewport.
    "@media (max-width: 499px)": {
      ...shorthands.borderLeft("0", "solid", "transparent"),
    },
  },
  /* Panel header bar — title + close button. */
  header: {
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    fontFamily: "'Segoe UI', sans-serif",
    fontWeight: "600",
    fontSize: "14px",
    lineHeight: "20px",
    height: "48px",
    paddingLeft: "16px",
    paddingRight: "8px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    // 1px stroke divider — Fluent's canonical pattern for delineating a
    // Dialog/Drawer header from the body content. Adapts to theme.
    ...shorthands.borderBottom("1px", "solid", tokens.colorNeutralStroke2),
    flexShrink: 0,
  },
  /* Tablet-only backdrop scrim.
   *
   * Mobile (<500): chat is fullscreen; no backdrop possible / wanted.
   * Tablet (500–1239): right-anchored panel covers ~50%; backdrop dims
   *   the rest of the page so the panel reads as an overlay and gives
   *   the user a click target to dismiss.
   * Desktop (>=1240): no backdrop — the user can keep interacting with
   *   the underlying app, matching how the panel behaved before.
   *
   * Sits at z-index 199, one below the chat panel at 200. */
  backdrop: {
    display: "none",
    "@media (min-width: 500px) and (max-width: 1239px)": {
      display: "block",
      position: "fixed",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      zIndex: 199,
      cursor: "pointer",
    },
  },
  headerTitle: {
    color: tokens.colorNeutralForeground1,
    fontSize: "14px",
    fontWeight: 600,
  },
  // Right-aligned wrapper used by the BuildingListView (the /buildings
  // landing route does NOT render NavigationHeader, so it needs its own
  // local mount of the WebChatToggleButton — see the comment in
  // BuildingListView.tsx for the rationale.)
  toggleButtonWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    paddingRight: "15px",
  },
  headerCloseButton: {
    backgroundColor: "transparent",
    ...shorthands.border("none"),
    color: tokens.colorNeutralForeground2,
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    ...shorthands.borderRadius("4px"),
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2,
      color: tokens.colorNeutralForeground1,
    },
  },
  /* Greeting section */
  greeting: {
    display: "flex",
    flexDirection: "column",
    // 24px gap below the header's 1px stroke divider — gives the 'Hello'
    // line clear breathing room from the title bar instead of stacking
    // tightly against it.
    paddingTop: "24px",
    paddingBottom: "8px",
    paddingLeft: "20px",
    paddingRight: "20px",
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  greetingTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: tokens.colorNeutralForeground1,
    marginTop: "0",
    marginBottom: "6px",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  greetingSubtitle: {
    fontSize: "14px",
    fontWeight: "400",
    color: tokens.colorNeutralForeground2,
    marginTop: "2px",
    marginBottom: "0",
  },
  /* Webchat wrapper + content */
  webchatWrapper: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minHeight: "0",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  webchat: {
    position: "relative",
    flexGrow: 1,
    overflowY: "hidden",
    minHeight: "0",
    "& ::-webkit-scrollbar": {
      width: "4px",
    },
    "& ::-webkit-scrollbar-thumb": {
      backgroundColor: tokens.colorNeutralBackground1,
      ...shorthands.borderRadius("4px"),
    },
    "& ::-webkit-scrollbar-thumb:hover": {
      backgroundColor: tokens.colorNeutralForeground4,
    },
    "& ::-webkit-scrollbar-track": {
      backgroundColor: tokens.colorNeutralBackground1,
    },
    "& *": {
      display: "revert",
      boxSizing: "border-box",
    },
    "& > *": {
      display: "block",
      height: "100%",
      width: "100%",
    },

    /* Markdown */
    "& p": {
      marginTop: "0",
      marginBottom: "8px",
      lineHeight: "1.5",
      fontSize: "14px",
    },
    "& p:last-child": {
      marginBottom: "0",
    },
    "& ol, & ul": {
      marginTop: "4px",
      marginBottom: "8px",
      paddingLeft: "20px",
    },
    "& li": {
      marginBottom: "4px",
      lineHeight: "1.5",
      fontSize: "14px",
    },
    "& h1, & h2, & h3, & h4, & h5, & h6": {
      marginTop: "12px",
      marginBottom: "6px",
      fontWeight: "600",
      lineHeight: "1.3",
    },
    "& h1": { fontSize: "18px" },
    "& h2": { fontSize: "16px" },
    "& h3": { fontSize: "15px" },
    "& a": {
      color: tokens.colorBrandForegroundLink,
      textDecorationLine: "underline",
    },
    "& a:hover": {
      color: tokens.colorBrandForegroundLinkHover,
    },
    "& code": {
      backgroundColor: tokens.colorNeutralBackground3,
      ...shorthands.borderRadius("3px"),
      paddingLeft: "4px",
      paddingRight: "4px",
      paddingTop: "2px",
      paddingBottom: "2px",
      fontSize: "13px",
      fontFamily: "Consolas, 'Courier New', monospace",
    },
    "& pre": {
      backgroundColor: tokens.colorNeutralBackground3,
      ...shorthands.borderRadius("6px"),
      ...shorthands.padding("10px"),
      overflowX: "auto",
      marginTop: "4px",
      marginBottom: "8px",
    },
    "& pre code": {
      backgroundColor: "transparent",
      ...shorthands.padding("0px"),
    },
    "& blockquote": {
      ...shorthands.borderLeft("3px", "solid", tokens.colorBrandForeground1),
      marginLeft: "0",
      paddingLeft: "12px",
      color: tokens.colorNeutralForeground3,
    },
  },
  /* Retry button row — right-aligned above send box */
  retryRow: {
    display: "flex",
    justifyContent: "flex-end",
    paddingRight: "12px",
    paddingTop: "4px",
    paddingBottom: "4px",
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  /* Send box row — single row with all controls */
  sendBoxRow: {
    display: "flex",
    alignItems: "center",
    height: "52px",
    marginLeft: "12px",
    marginRight: "12px",
    // Reserve room for the iPhone home indicator / Android gesture bar. On
    // devices without a safe-area-inset the env(...) value resolves to 0,
    // so this collapses to the original 12px margin. Mirrors the idiom
    // already used in CartItemForm and MenuItemDetails.
    marginBottom: "calc(12px + env(safe-area-inset-bottom))",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.gap("8px"),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("8px"),
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },
  sendBoxInput: {
    flexGrow: 1,
    height: "40px",
    ...shorthands.border("none"),
    ...shorthands.borderRadius("0px"),
    paddingLeft: "4px",
    paddingRight: "4px",
    fontSize: "14px",
    color: tokens.colorNeutralForeground1,
    backgroundColor: "transparent",
    outlineStyle: "none",
    "::placeholder": {
      color: tokens.colorNeutralForeground4,
      fontSize: "14px",
    },
  },
  toolbarButton: {
    backgroundColor: "transparent",
    ...shorthands.border("none"),
    color: tokens.colorNeutralForeground2,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "32px",
    height: "32px",
    ...shorthands.borderRadius("4px"),
    flexShrink: 0,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground2,
      color: tokens.colorNeutralForeground1,
    },
    // Visual feedback for the native `disabled` attribute — without this
    // the send button at full opacity invites clicks when the input is
    // empty/whitespace and nothing happens (WCAG 2.1 SC 1.4.1 — disabled
    // state must be perceptible by more than behaviour alone).
    ":disabled": {
      opacity: 0.4,
      cursor: "not-allowed",
      ":hover": {
        backgroundColor: "transparent",
        color: tokens.colorNeutralForeground2,
      },
    },
  },
  /* Error / loading states */
  stateContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flexGrow: 1,
    ...shorthands.padding("24px"),
    textAlign: "center" as const,
    color: tokens.colorNeutralForeground2,
    fontSize: "14px",
  },
  retryButton: {
    marginTop: "12px",
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", tokens.colorBrandForeground1),
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandForeground1,
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    ":hover": {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
});
